// Compliance centre overview: launch readiness, what needs attention, and
// the honest headline about where ConfirmPH actually stands.
import { useEffect, useState } from "react";
import { AlertTriangle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import {
  CAP, LAUNCH_STATE_LABELS, can, humanise, launchTone, legalError, plGet,
} from "../../lib/platformLegal";
import { AlertRow, Empty, Loading, Note, Section, Stat, TonePill } from "./ui";

export default function OverviewSection({ capabilities, onOpenTab }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    plGet("/overview")
      .then(setData)
      .catch((err) => toast.error(legalError(err)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading label="Reading the compliance registers..." />;
  if (!data) return <Empty>The compliance overview could not be loaded.</Empty>;

  const launch = data.launch;
  const alerts = data.alerts?.alerts || [];

  return (
    <div className="space-y-4">
      <Section testId="pl-launch-summary"
               title="Public launch readiness"
               description="ConfirmPH's own internal assessment. It is not a legal opinion and not a
                            government clearance.">
        <div className="flex flex-wrap items-center gap-2">
          <TonePill tone={launchTone(launch.state)} testId="pl-launch-state">
            {LAUNCH_STATE_LABELS[launch.state] || launch.state}
          </TonePill>
          <TonePill tone="border-slate-200 bg-slate-50 text-slate-600">
            Gate: {launch.gate.mode}
          </TonePill>
        </div>
        <p className="mt-2 text-sm text-slate-700">{launch.statement}</p>
        {!launch.gate.enforced && (
          <Note tone="slate" testId="pl-gate-note">
            The production gate is in observe mode: readiness is reported and nothing is blocked.
            Development and staging are never blocked by this engine.
          </Note>
        )}

        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          {Object.entries(launch.by_category).map(([category, counts]) => (
            <Stat key={category} label={humanise(category)}
                  value={`${counts.satisfied}/${counts.total}`}
                  tone={counts.satisfied === counts.total ? "text-emerald-700" : "text-slate-900"}
                  testId={`pl-launch-cat-${category}`} />
          ))}
        </div>

        {launch.production_blockers?.length > 0 && (
          <div className="mt-4">
            <div className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
              Outstanding before public production ({launch.production_blockers.length})
            </div>
            <ul className="mt-2 flex flex-wrap gap-1.5">
              {launch.production_blockers.map((key) => (
                <li key={key}
                    className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs
                               text-amber-900">
                  {humanise(key)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Section>

      <Section testId="pl-alerts"
               title="Needs attention"
               description="Computed from the registers themselves, filtered to what you can act on.">
        {alerts.length === 0 ? (
          <Empty testId="pl-alerts-empty">
            Nothing is outstanding in the areas you have access to.
          </Empty>
        ) : (
          <div className="space-y-2">
            {alerts.map((alert) => (
              <AlertRow key={alert.key} alert={alert} onOpen={onOpenTab} />
            ))}
          </div>
        )}
      </Section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section testId="pl-identity-summary" title="Corporate identity">
          <div className="grid grid-cols-3 gap-2">
            <Stat label="Recorded" value={data.identity.total} />
            <Stat label="Verified" value={data.identity.verified}
                  tone={data.identity.verified ? "text-emerald-700" : "text-slate-900"} />
            <Stat label="Not configured" value={data.identity.not_configured}
                  tone="text-amber-700" />
          </div>
          {data.identity.external_actions_outstanding?.length > 0 && (
            <p className="mt-3 text-xs text-slate-600">
              <span className="font-semibold">Outside ConfirmPH:</span>{" "}
              {data.identity.external_actions_outstanding.slice(0, 6).join(", ")}
              {data.identity.external_actions_outstanding.length > 6 ? "…" : ""}
            </p>
          )}
        </Section>

        <Section testId="pl-merchant-summary" title="Merchants">
          <div className="grid grid-cols-3 gap-2">
            <Stat label="Businesses" value={data.merchants.total} />
            <Stat label="Identity verified"
                  value={(data.merchants.by_state?.OPERATIONAL || 0)
                         + (data.merchants.by_state?.BUSINESS_IDENTITY_VERIFIED || 0)} />
            <Stat label="Reverification due" value={data.merchants.reverification_overdue}
                  tone={data.merchants.reverification_overdue ? "text-amber-700" : "text-slate-900"} />
          </div>
          <Note tone="slate" testId="pl-merchant-note">
            Identity verification is not an assessment of a business's legal compliance,
            licensing, insurance or vehicle condition. ConfirmPH does not make that assessment.
          </Note>
        </Section>

        {can(capabilities, CAP.complaints) && (
          <Section testId="pl-complaints-summary" title="Consumer complaints">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Stat label="Open" value={data.complaints.open} />
              <Stat label="Overdue" value={data.complaints.overdue}
                    tone={data.complaints.overdue ? "text-rose-700" : "text-slate-900"} />
              <Stat label="Due 24h" value={data.complaints.due_24h} />
              <Stat label="Urgent" value={data.complaints.urgent} />
            </div>
            <Note tone="slate">{data.complaints.liability_note}</Note>
          </Section>
        )}

        {can(capabilities, CAP.safety) && (
          <Section testId="pl-safety-summary" title="Urgent safety reports">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Stat label="Open" value={data.safety_reports.open} />
              <Stat label="Untriaged" value={data.safety_reports.untriaged}
                    tone={data.safety_reports.untriaged ? "text-rose-700" : "text-slate-900"} />
              <Stat label="Critical" value={data.safety_reports.critical} />
              <Stat label="With holds" value={data.safety_reports.with_holds} />
            </div>
          </Section>
        )}

        {data.privacy && (
          <Section testId="pl-privacy-summary" title="Privacy">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Stat label="DPO" value={data.privacy.dpo?.status === "appointed" ? "Appointed" : "None"}
                    tone={data.privacy.dpo?.status === "appointed"
                      ? "text-emerald-700" : "text-amber-700"} />
              <Stat label="PIAs approved"
                    value={`${data.privacy.pia.approved}/${data.privacy.pia.total}`} />
              <Stat label="Roles determined"
                    value={`${data.privacy.data_roles.determined}/${data.privacy.data_roles.total}`} />
              <Stat label="Requests open" value={data.privacy_requests?.open ?? 0} />
            </div>
          </Section>
        )}

        <Section testId="pl-storage-summary" title="Document storage">
          <div className="flex flex-wrap items-center gap-2">
            <TonePill tone={data.storage.durable
              ? "border-emerald-200 bg-emerald-50 text-emerald-800"
              : "border-amber-200 bg-amber-50 text-amber-900"}>
              {data.storage.provider}
            </TonePill>
            {data.storage.production_blocker && (
              <span className="inline-flex items-center gap-1 text-xs text-amber-800">
                <AlertTriangle className="w-3.5 h-3.5" /> Production blocker
              </span>
            )}
          </div>
          {data.storage.note && <Note tone="amber" testId="pl-storage-note">{data.storage.note}</Note>}
        </Section>
      </div>

      <Section testId="pl-principle">
        <div className="flex items-start gap-2">
          <ShieldCheck className="w-5 h-5 shrink-0 text-slate-500 mt-0.5" />
          <p className="text-sm text-slate-700">
            Everything in this centre records what ConfirmPH can prove. Nothing here incorporates
            a company, registers with an agency, appoints a Data Protection Officer, engages
            counsel, issues shares, or notifies a regulator — those are real-world acts, tracked
            here and marked <span className="font-semibold">ACTION REQUIRED OUTSIDE CONFIRMPH</span>.
          </p>
        </div>
      </Section>
    </div>
  );
}
